import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, CheckCircleIcon, XCircleIcon, DollarSignIcon, FileTextIcon, CalendarIcon, UserIcon, AlertCircleIcon } from 'lucide-react';
import { Bill } from '../types/Bill';
export function BillDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const bill: Bill = {
    id: id || '1',
    billNumber: 'BILL-2024-001',
    supplierId: '1',
    supplierName: 'Premium Tea Estates Ltd',
    supplierEmail: 'rajesh@premiumtea.lk',
    supplierPhone: '+94 77 123 4567',
    amount: 125000,
    currency: 'LKR',
    status: 'pending_approval',
    dueDate: '2024-02-15',
    issueDate: '2024-01-15',
    description: 'Ceylon Black Tea - January Order',
    items: [{
      id: '1',
      description: 'Ceylon Black Tea - Grade A',
      quantity: 500,
      unitPrice: 200,
      total: 100000
    }, {
      id: '2',
      description: 'Packaging & Handling',
      quantity: 1,
      unitPrice: 25000,
      total: 25000
    }],
    notes: 'Please ensure payment is made before due date to maintain supplier relationship.',
    taxAmount: 0,
    discountAmount: 0,
    subtotal: 125000,
    total: 125000,
    approvalStatus: 'pending',
    financingStatus: 'not_requested'
  };
  const getStatusBadge = (status: Bill['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const labels = {
      draft: 'Draft',
      pending_approval: 'Pending Approval',
      approved: 'Approved',
      paid: 'Paid',
      overdue: 'Overdue',
      rejected: 'Rejected'
    };
    return <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles[status]}`} data-id="element-2843">
        {labels[status]}
      </span>;
  };
  return <div className="space-y-6" data-id="element-2844">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-2845">
        <button onClick={() => navigate('/bills')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-2846">
          <ArrowLeftIcon size={20} data-id="element-2847" />
          Back to Bills
        </button>
        <div className="flex items-center gap-3" data-id="element-2848">
          {bill.status === 'draft' && <button onClick={() => navigate(`/bill-form?id=${bill.id}`)} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-2849">
              <EditIcon size={18} data-id="element-2850" />
              Edit Bill
            </button>}
          {bill.status === 'pending_approval' && <button onClick={() => navigate(`/bill-approval/${bill.id}`)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" data-id="element-2851">
              <CheckCircleIcon size={18} data-id="element-2852" />
              Review & Approve
            </button>}
          {bill.status === 'approved' && bill.financingStatus === 'not_requested' && <button onClick={() => navigate(`/bill-financing/${bill.id}`)} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-2853">
                <DollarSignIcon size={18} data-id="element-2854" />
                Request Financing
              </button>}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2855">
        {/* Left Column - Bill Details */}
        <div className="lg:col-span-2 space-y-6" data-id="element-2856">
          {/* Bill Info Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2857">
            <div className="flex items-start justify-between mb-6" data-id="element-2858">
              <div data-id="element-2859">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-2860">
                  {bill.billNumber}
                </h1>
                <p className="text-sm text-gray-500 mt-1" data-id="element-2861">{bill.description}</p>
              </div>
              {getStatusBadge(bill.status)}
            </div>
            {/* Supplier Info */}
            <div className="mb-6 pb-6 border-b border-gray-200" data-id="element-2862">
              <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-2863">
                SUPPLIER
              </h3>
              <div className="space-y-2" data-id="element-2864">
                <div className="text-base font-medium text-gray-900" data-id="element-2865">
                  {bill.supplierName}
                </div>
                <div className="text-sm text-gray-600" data-id="element-2866">
                  {bill.supplierEmail}
                </div>
                {bill.supplierPhone && <div className="text-sm text-gray-600" data-id="element-2867">
                    {bill.supplierPhone}
                  </div>}
              </div>
              <button onClick={() => navigate(`/supplier-detail/${bill.supplierId}`)} className="mt-3 text-sm text-blue-600 hover:text-blue-800" data-id="element-2868">
                View Supplier Details →
              </button>
            </div>
            {/* Bill Items */}
            <div className="mb-6" data-id="element-2869">
              <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-2870">ITEMS</h3>
              <div className="space-y-3" data-id="element-2871">
                {bill.items.map(item => <div key={item.id} className="flex items-center justify-between py-3 border-b border-gray-100" data-id="element-2872">
                    <div className="flex-1" data-id="element-2873">
                      <div className="text-sm font-medium text-gray-900" data-id="element-2874">
                        {item.description}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-2875">
                        {item.quantity} × Rs.{' '}
                        {item.unitPrice.toLocaleString('en-LK')}
                      </div>
                    </div>
                    <div className="text-sm font-medium text-gray-900" data-id="element-2876">
                      Rs. {item.total.toLocaleString('en-LK')}
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Totals */}
            <div className="space-y-2" data-id="element-2877">
              <div className="flex justify-between text-sm" data-id="element-2878">
                <span className="text-gray-600" data-id="element-2879">Subtotal:</span>
                <span className="text-gray-900" data-id="element-2880">
                  Rs. {bill.subtotal.toLocaleString('en-LK')}
                </span>
              </div>
              {bill.taxAmount && bill.taxAmount > 0 && <div className="flex justify-between text-sm" data-id="element-2881">
                  <span className="text-gray-600" data-id="element-2882">Tax:</span>
                  <span className="text-gray-900" data-id="element-2883">
                    Rs. {bill.taxAmount.toLocaleString('en-LK')}
                  </span>
                </div>}
              {bill.discountAmount && bill.discountAmount > 0 && <div className="flex justify-between text-sm" data-id="element-2884">
                  <span className="text-gray-600" data-id="element-2885">Discount:</span>
                  <span className="text-red-600" data-id="element-2886">
                    -Rs. {bill.discountAmount.toLocaleString('en-LK')}
                  </span>
                </div>}
              <div className="flex justify-between pt-3 border-t-2 border-gray-200" data-id="element-2887">
                <span className="text-lg font-semibold text-gray-900" data-id="element-2888">
                  Total:
                </span>
                <span className="text-lg font-bold text-gray-900" data-id="element-2889">
                  Rs. {bill.total.toLocaleString('en-LK')}
                </span>
              </div>
            </div>
            {/* Notes */}
            {bill.notes && <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-2890">
                <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-2891">
                  NOTES
                </h3>
                <p className="text-sm text-gray-700" data-id="element-2892">{bill.notes}</p>
              </div>}
          </div>
        </div>
        {/* Right Column - Status & Actions */}
        <div className="space-y-6" data-id="element-2893">
          {/* Dates */}
          <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2894">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-2895">Dates</h3>
            <div className="space-y-4" data-id="element-2896">
              <div data-id="element-2897">
                <div className="flex items-center text-gray-500 text-sm mb-1" data-id="element-2898">
                  <CalendarIcon size={16} className="mr-2" data-id="element-2899" />
                  Issue Date
                </div>
                <p className="text-sm font-medium text-gray-900" data-id="element-2900">
                  {new Date(bill.issueDate).toLocaleDateString('en-GB')}
                </p>
              </div>
              <div data-id="element-2901">
                <div className="flex items-center text-gray-500 text-sm mb-1" data-id="element-2902">
                  <CalendarIcon size={16} className="mr-2" data-id="element-2903" />
                  Due Date
                </div>
                <p className="text-sm font-medium text-gray-900" data-id="element-2904">
                  {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                </p>
              </div>
              {bill.paidDate && <div data-id="element-2905">
                  <div className="flex items-center text-gray-500 text-sm mb-1" data-id="element-2906">
                    <CalendarIcon size={16} className="mr-2" data-id="element-2907" />
                    Paid Date
                  </div>
                  <p className="text-sm font-medium text-gray-900" data-id="element-2908">
                    {new Date(bill.paidDate).toLocaleDateString('en-GB')}
                  </p>
                </div>}
            </div>
          </div>
          {/* Approval Status */}
          {bill.approvalStatus && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2909">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-2910">
                Approval Status
              </h3>
              <div className="space-y-4" data-id="element-2911">
                <div data-id="element-2912">
                  <div className="flex items-center gap-2 mb-2" data-id="element-2913">
                    {bill.approvalStatus === 'pending' && <AlertCircleIcon size={20} className="text-yellow-500" data-id="element-2914" />}
                    {bill.approvalStatus === 'approved' && <CheckCircleIcon size={20} className="text-green-500" data-id="element-2915" />}
                    {bill.approvalStatus === 'rejected' && <XCircleIcon size={20} className="text-red-500" data-id="element-2916" />}
                    <span className="text-sm font-medium text-gray-900" data-id="element-2917">
                      {bill.approvalStatus === 'pending' && 'Pending Approval'}
                      {bill.approvalStatus === 'approved' && 'Approved'}
                      {bill.approvalStatus === 'rejected' && 'Rejected'}
                    </span>
                  </div>
                  {bill.approver && <div data-id="element-2918">
                      <div className="flex items-center text-gray-500 text-sm mb-1" data-id="element-2919">
                        <UserIcon size={16} className="mr-2" data-id="element-2920" />
                        Approver
                      </div>
                      <p className="text-sm font-medium text-gray-900" data-id="element-2921">
                        {bill.approver}
                      </p>
                    </div>}
                  {bill.approvalDate && <div className="mt-2" data-id="element-2922">
                      <div className="flex items-center text-gray-500 text-sm mb-1" data-id="element-2923">
                        <CalendarIcon size={16} className="mr-2" data-id="element-2924" />
                        Approval Date
                      </div>
                      <p className="text-sm font-medium text-gray-900" data-id="element-2925">
                        {new Date(bill.approvalDate).toLocaleDateString('en-GB')}
                      </p>
                    </div>}
                  {bill.approvalNotes && <div className="mt-2" data-id="element-2926">
                      <p className="text-sm text-gray-600" data-id="element-2927">
                        {bill.approvalNotes}
                      </p>
                    </div>}
                </div>
              </div>
            </div>}
          {/* Financing Status */}
          {bill.financingStatus && bill.financingStatus !== 'not_requested' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2928">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-2929">
                Financing Status
              </h3>
              <div className="space-y-4" data-id="element-2930">
                <div data-id="element-2931">
                  <div className="text-sm text-gray-500 mb-1" data-id="element-2932">Status</div>
                  <p className="text-sm font-medium text-gray-900 capitalize" data-id="element-2933">
                    {bill.financingStatus.replace('_', ' ')}
                  </p>
                </div>
                {bill.financingProvider && <div data-id="element-2934">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-2935">Provider</div>
                    <p className="text-sm font-medium text-gray-900" data-id="element-2936">
                      {bill.financingProvider}
                    </p>
                  </div>}
                {bill.financingAmount && <div data-id="element-2937">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-2938">Amount</div>
                    <p className="text-sm font-medium text-gray-900" data-id="element-2939">
                      Rs. {bill.financingAmount.toLocaleString('en-LK')}
                    </p>
                  </div>}
              </div>
            </div>}
        </div>
      </div>
    </div>;
}